import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  BookOpen,
  Edit,
  Trash2,
  Eye,
  Globe,
  GlobeLock,
  MoreVertical,
  Loader2,
  Filter,
  FolderOpen,
  Clock,
  Users,
  FileVideo,
  FileText,
  CheckCircle2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function CourseBuilderList() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Category creation
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [creatingCategory, setCreatingCategory] = useState(false);
  
  // New course form
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    category_id: '',
    passing_score: 75,
    tags: ''
  });

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category_id', categoryFilter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/tech-learning/builder/courses?${params}`);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, categoryFilter, search]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/tech-learning/builder/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      setCreatingCategory(true);
      const response = await api.post('/tech-learning/builder/categories', newCategory);
      
      toast.success('Category created successfully');
      setShowCategoryDialog(false);
      setNewCategory({ name: '', description: '' });
      
      // Refresh categories and auto-select the new one
      await fetchCategories();
      setNewCourse({ ...newCourse, category_id: response.data.category.id });
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCreateCourse = async () => {
    if (!newCourse.title.trim()) {
      toast.error('Course title is required');
      return;
    }
    if (!newCourse.category_id) {
      toast.error('Please select a category');
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/tech-learning/builder/courses', {
        ...newCourse,
        tags: newCourse.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      
      toast.success('Course created successfully');
      setShowCreateDialog(false);
      setNewCourse({ title: '', description: '', category_id: '', passing_score: 75, tags: '' });
      
      // Navigate to course editor
      navigate(`/admin/course-builder/${response.data.course.id}`);
    } catch (error) {
      console.error('Failed to create course:', error);
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;
    
    try {
      await api.delete(`/tech-learning/builder/courses/${selectedCourse.id}`);
      toast.success('Course deleted');
      setShowDeleteDialog(false);
      setSelectedCourse(null);
      fetchCourses();
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handlePublish = async (course) => {
    try {
      if (course.status === 'published') {
        await api.post(`/tech-learning/builder/courses/${course.id}/unpublish`);
        toast.success('Course unpublished');
      } else {
        await api.post(`/tech-learning/builder/courses/${course.id}/publish`);
        toast.success('Course published');
      }
      fetchCourses();
    } catch (error) {
      console.error('Failed to update publish status:', error);
      const detail = error.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.errors.join(', '));
      } else {
        toast.error(detail || 'Failed to update course');
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-emerald-500">Published</Badge>;
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'archived':
        return <Badge variant="outline">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="course-builder-list">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Course Builder</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage tech learning courses
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-course-btn">
            <Plus className="h-4 w-4" />
            Create Course
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search courses..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Course Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : courses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No courses yet</h3>
              <p className="text-muted-foreground text-center mt-1">
                Create your first course to get started
              </p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4 gap-2">
                <Plus className="h-4 w-4" />
                Create Course
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(course => (
              <Card 
                key={course.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`course-card-${course.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{course.title}</CardTitle>
                      <CardDescription className="line-clamp-2 mt-1">
                        {course.description || 'No description'}
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/course-builder/${course.id}`} className="flex items-center gap-2">
                            <Edit className="h-4 w-4" />
                            Edit Course
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/course-builder/${course.id}/preview`} className="flex items-center gap-2">
                            <Eye className="h-4 w-4" />
                            Preview
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handlePublish(course)} className="flex items-center gap-2">
                          {course.status === 'published' ? (
                            <>
                              <GlobeLock className="h-4 w-4" />
                              Unpublish
                            </>
                          ) : (
                            <>
                              <Globe className="h-4 w-4" />
                              Publish
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => { setSelectedCourse(course); setShowDeleteDialog(true); }}
                          className="text-destructive flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 mb-3">
                    {getStatusBadge(course.status)}
                    <Badge variant="outline" className="text-xs">
                      {course.category_name || 'Uncategorized'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div className="p-2 bg-muted/50 rounded-lg">
                      <p className="font-semibold">{course.module_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Modules</p>
                    </div>
                    <div className="p-2 bg-muted/50 rounded-lg">
                      <p className="font-semibold">{course.total_videos || 0}</p>
                      <p className="text-xs text-muted-foreground">Videos</p>
                    </div>
                    <div className="p-2 bg-muted/50 rounded-lg">
                      <p className="font-semibold">{course.total_assessments || 0}</p>
                      <p className="text-xs text-muted-foreground">Quizzes</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {course.enrolled_count || 0} enrolled
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {course.estimated_hours || 0}h
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {course.passing_score || 75}% pass
                    </div>
                  </div>

                  <Button 
                    asChild 
                    className="w-full mt-4" 
                    variant="outline"
                  >
                    <Link to={`/admin/course-builder/${course.id}`}>
                      Open Editor
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Course Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg" data-testid="create-course-dialog">
            <DialogHeader>
              <DialogTitle>Create New Course</DialogTitle>
              <DialogDescription>
                Set up the basic details for your new course
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Course Title *</label>
                <Input
                  value={newCourse.title}
                  onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                  placeholder="e.g., Introduction to Python"
                  className="mt-1"
                  data-testid="course-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                  placeholder="Brief description of the course..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Category *</label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs"
                    onClick={() => setShowCategoryDialog(true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    New Category
                  </Button>
                </div>
                <Select 
                  value={newCourse.category_id} 
                  onValueChange={(v) => setNewCourse({ ...newCourse, category_id: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="course-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Passing Score (%)</label>
                <Input
                  type="number"
                  min={50}
                  max={100}
                  value={newCourse.passing_score}
                  onChange={(e) => setNewCourse({ ...newCourse, passing_score: parseInt(e.target.value) || 75 })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Tags (comma-separated)</label>
                <Input
                  value={newCourse.tags}
                  onChange={(e) => setNewCourse({ ...newCourse, tags: e.target.value })}
                  placeholder="python, programming, beginner"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCourse} disabled={creating} data-testid="create-course-submit">
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Course
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Course</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{selectedCourse?.title}&quot;? This will also delete all modules, content, and assessments. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteCourse}>
                Delete Course
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Category Dialog */}
        <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Category</DialogTitle>
              <DialogDescription>
                Add a new category for organizing courses
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Category Name *</label>
                <Input
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., DevOps & Cloud"
                  className="mt-1"
                  data-testid="new-category-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description (optional)</label>
                <Textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  placeholder="Brief description of this category..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCategory} disabled={creatingCategory}>
                {creatingCategory && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
